/**
 * «Повторить заказ» — a 1:1 port of Android's `OrderRepeatPrefill`.
 *
 * Two invariants separate a repeat from a resume, and both matter:
 *
 * - **A brand-new `externalId`.** Repeating is a genuinely new order, never a retry of the old
 *   one. Reusing the key would make the server answer with the original order and no second
 *   delivery would ever happen.
 * - **The historical coordinates, resent as-is.** The backend re-resolves the zone from them at
 *   creation, so there is nothing to refresh client-side. `serviceAreaAvailable` is optimistically
 *   `true` — the order was accepted there once — and if the zone has genuinely changed since, the
 *   existing `service_area_unavailable` path handles it. No new error branch.
 *
 * Where the draft **lands** is the other half. A repeat exists to skip re-entering everything, so
 * it goes straight to the summary — unless something it depended on is gone, in which case it
 * lands on the step that can fix it.
 */
import type { ServiceArea } from '../serviceArea';
import { withCityWindows } from './deliveryWindow';
import { createOrderDraft, type ExtraCar, type OrderDraft, type OrderStep } from './draft';
import { isAsapOpen, withServiceHours } from './serviceHours';

/** The narrow projection of a historical order this builder needs. */
export interface HistoricalOrder {
  addressText: string;
  latitude: number;
  longitude: number;
  operatorComment: string;
  ownerPresent: boolean;
  /** Litres actually dispensed. `null` for an order cancelled before it was ever fuelled. */
  fuelAmountLiters: string | null;
  /**
   * Litres actually **requested** at creation, frozen forever.
   *
   * Preferred for a repeat: a cancelled order has no billed amount at all, and even a completed
   * one's billed amount can differ from what was asked. Intent is what is being repeated.
   */
  requestedFuelLiters: string | null;
}

export interface RepeatCar {
  id: string;
  label: string;
  brand: string;
  fuelTypeCode: string;
  tankVolumeLiters: string | null;
}

export interface RepeatPaymentMethod {
  id: string;
  label: string;
}

/**
 * `car` is `null` when the historical car no longer exists — the draft then carries no car and
 * lands on the address step, and the caller routes through the picker first. `paymentMethod` is
 * `null` when the saved card is gone: the draft keeps its car but lands on payment rather than
 * jumping to the summary, so a live card is chosen before the final review.
 *
 * `extraCars` are the other cars of the historical выезд, already resolved against the current
 * garage by the caller. Carried through so repeating a three-car выезд produces a three-car one —
 * it is easy to silently produce a single-car order instead, and that is a quiet failure: somebody
 * taps «Повторить» precisely because they do not want to re-enter it.
 */
export function buildRepeatDraft(
  order: HistoricalOrder,
  car: RepeatCar | null,
  paymentMethod: RepeatPaymentMethod | null,
  extraCars: ExtraCar[] = [],
): OrderDraft {
  const liters = order.requestedFuelLiters ?? order.fuelAmountLiters;

  return createOrderDraft({
    carId: car?.id ?? null,
    carLabel: car?.label ?? null,
    carBrand: car?.brand ?? '',
    // Extras hang off a primary car; with none there is nothing to hang them on.
    extraCars: car === null ? [] : extraCars,
    fuelTypeCode: car?.fuelTypeCode ?? null,
    tankVolumeLiters: car?.tankVolumeLiters ?? null,
    latitude: order.latitude,
    longitude: order.longitude,
    addressLabel: order.addressText,
    serviceAreaAvailable: true,
    comment: order.operatorComment,
    fuelMode: liters !== null ? 'liters' : 'fullTank',
    liters: liters ?? '',
    presence: order.ownerPresent ? 'withMe' : 'withoutMe',
    paymentMethodId: paymentMethod?.id ?? null,
    paymentMethodLabel: paymentMethod?.label ?? null,
    step: landingStep(car !== null, paymentMethod !== null),
  });
}

function landingStep(hasCar: boolean, hasPaymentMethod: boolean): OrderStep {
  if (!hasCar) return 'address';
  if (!hasPaymentMethod) return 'payment';
  return 'summary';
}

/**
 * Часы работы для повтора — по ответу проверки зоны той же точки.
 *
 * Повтор прыгает сразу на сводку со скоростью по умолчанию — «Ближайшей». Ночью это значило бы
 * отказ сервера на кнопке оплаты (решение владельца 30.09.2026: «Срочно» и «Ближайшая» только в
 * часы работы). Поэтому, когда приём сейчас закрыт, повтор приземляется на выбор времени с уже
 * выбранным «Ко времени»: окно — единственное, чего в повторе не хватает.
 *
 * Нет ответа (сбой связи) или точка вне зоны — черновик остаётся как был: часы и зону всё равно
 * проверит сервер при оформлении, а прятать повтор из-за упавшей проверки незачем.
 */
export function withRepeatHours(
  draft: OrderDraft,
  area: ServiceArea | null,
  now: Date = new Date(),
): OrderDraft {
  if (area === null || !area.available) return draft;
  const withArea = withServiceHours(
    withCityWindows(draft, area.deliveryWindows, area.dispatchLeadMinutes),
    area.serviceHours,
    now,
  );
  const pastTheTimeStep = withArea.step === 'payment' || withArea.step === 'summary';
  if (pastTheTimeStep && !isAsapOpen(area.serviceHours, now)) {
    return { ...withArea, step: 'time' };
  }
  return withArea;
}
